/* eslint-disable react-hooks/exhaustive-deps */
import { Text, View } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { IMaterialsResult, IProjectResultResponse } from '../../../MACRA_1/api/IApiResponse';
import { SubSectorPosition } from '../../../MACRA_1/enum/Sector';
import { IProjectMacra1 } from '../../../MACRA_1/store/project/projectSlice';
import { conversionLabel } from '../../../conversion';
import { IProjectInfoForm } from '../../../pages/ProjectInfo/IProjectInfo';
import SVGCheck from '../PDFLayout/SVGCheck';
import SVGX from '../PDFLayout/SVGX';
import SVGWarning from '../PDFLayout/SVGWarning';
import { isConcrete } from '../../../enum/Material';
import { styles } from '../PDFStyle';

interface IPDFTableProps {
    results: IProjectResultResponse;
    project: IProjectMacra1;
    projectInfo: IProjectInfoForm;
}

const PDFTable = (props: IPDFTableProps) => {
    const { t } = useTranslation();
    const { results, projectInfo } = props;
    let sortedA: IMaterialsResult[] = [];
    if (!!results && !!results.materialsResult.length) {
        let newArray = [...results.materialsResult];
        sortedA = newArray.sort((a, b) => {
            if (a.channel === b.channel) {
                if ((a.subSectorPosition === 0 || !!a.subSectorPosition) && (b.subSectorPosition === 0 || !!b.subSectorPosition)) {
                    if (a.subSectorPosition === SubSectorPosition.Left) {
                        return -1;
                    }
                    if (a.subSectorPosition === SubSectorPosition.Center && b.subSectorPosition === SubSectorPosition.Left) {
                        return 1;
                    }
                    if (a.subSectorPosition === SubSectorPosition.Center && b.subSectorPosition === SubSectorPosition.Right) {
                        return -1;
                    }
                    return 0;
                }
            }
            return a.channel - b.channel;
        });
    }
    return (
        <View break>
            <View style={styles.rowSpaceBetween}>
                <Text style={styles.boldGreen}>{t('modal_material_title')}</Text>
                <Text style={styles.note}>/ {t('pdf_warning')}</Text>
            </View>
            <View style={[styles.row, styles.borderRow, styles.headerTable]}>
                <Text style={{ width: '4%' }}>{t('material_id')}</Text>
                <Text style={{ width: '21%' }}>{t('graphical_results_materials')}</Text>
                <Text style={{ width: '6%' }}>{t('results_material_thickness', { unit_measure: conversionLabel.m_in[projectInfo.unitOfMeasure as number] })}</Text>
                <Text style={{ width: '6%' }}>{t('material_roughness')}</Text>
                <Text style={{ width: '6%' }}>{t('results_material_rock', { unit_measure: conversionLabel.m_in[projectInfo?.unitOfMeasure as number] })}</Text>
                <Text style={{ width: '2%' }}>{t('material_cu')}</Text>
                <Text style={{ width: '6%' }}>{t('results_material_castoro')}</Text>
                <Text style={{ width: '6%' }}>{t('material_xties')}</Text>
                <Text style={{ width: '6%' }}>{t('results_material_geotextile')}</Text>
                <Text style={{ width: '7%' }}>{t('material_time', { unit_measure: 'h' })}</Text>
                <View style={{ width: '16%' }}>
                    <Text>{t('results_material_shear_stress', { unit_measure: conversionLabel.Nm2_lbfft2[projectInfo?.unitOfMeasure as number] })}</Text>
                    <View style={[styles.row, styles.justifyContentAround]}>
                        <Text>{t('results_allowed')}</Text>
                        <Text>{t('results_calculated')}</Text>
                        <View style={{ width: '10px' }}></View>
                    </View>
                </View>
                <Text style={{ width: '8%' }}>{t('results_material_velocity', { unit_measure: conversionLabel.ms_fts[projectInfo?.unitOfMeasure as number] })}</Text>
                <Text style={{ width: '6%' }}>{t('modal_subsector_subsector_length', { unit_measure: conversionLabel.m_ft[projectInfo?.unitOfMeasure as number] })}</Text>
            </View>
            {!!sortedA &&
                sortedA.map((rowData: IMaterialsResult, index: number) => {
                    let channelAndPosition = rowData.channel.toString();
                    if (rowData.subSectorPosition !== undefined) {
                        let character;
                        switch (rowData.subSectorPosition) {
                            case SubSectorPosition.Left:
                                character = t('results_position_left');
                                break;
                            case SubSectorPosition.Center:
                                character = t('results_position_center');
                                break;
                            case SubSectorPosition.Right:
                                character = t('results_position_right');
                                break;
                        }
                        channelAndPosition = `${rowData.channel}.${character}`;
                    }
                    let color = index % 2 == 0 ? '#dee0e1' : '#fff';
                    let bedMaterial = rowData.bedMaterial.replace('ᵀᴹ', '\u2122');
                    return (
                        <View style={[styles.row, styles.borderRow, { backgroundColor: color }]} key={index}>
                            <Text style={[{ width: '4%' }]}>{channelAndPosition}</Text>
                            <Text style={{ width: '21%', textAlign: 'left', paddingLeft: '5px' }}>{bedMaterial}</Text>
                            <Text style={{ width: '6%' }}>{rowData.thickness}</Text>
                            <Text style={{ width: '6%' }}>{rowData.roughness}</Text>
                            <Text style={{ width: '6%' }}>{rowData.rockD50}</Text>
                            <Text style={{ width: '2%' }}>{rowData.cu}</Text>
                            <Text style={{ width: '6%' }}>{rowData.castoro ? t('yes') : ''}</Text>
                            <Text style={{ width: '6%' }}>{rowData.xTies ? t('yes') : ''}</Text>
                            <Text style={{ width: '6%' }}>{rowData.geotextile ? t('yes') : ''}</Text>
                            <Text style={{ width: '7%' }}>{rowData.time}</Text>
                            <View style={[styles.row, styles.justifyContentAround, { width: '16%' }]}>
                                <Text>{isConcrete(rowData.category) ? '—' : rowData.allowedShearStress.toFixed(2)}</Text>
                                <Text>{rowData.calculatedShearStress.toFixed(2)}</Text>
                                {isConcrete(rowData.category) ? <SVGWarning /> : rowData.isDangerousShearStress ? <SVGX /> : <SVGCheck />}
                            </View>
                            <View style={[styles.row, styles.justifyContentAround, { width: '8%' }]}>
                                <Text>{rowData.velocityUnderLining === 0 ? '-' : rowData.velocityUnderLining.toFixed(2)}</Text>
                                {rowData.velocityUnderLining === 0 ? <Text>-</Text> : rowData.isDangerousVelocityUnderLining ? <SVGX /> : <SVGCheck />}
                            </View>
                            <Text style={{ width: '6%' }}>{rowData.length.toFixed(2)}</Text>
                        </View>
                    );
                })}
        </View>
    );
};
export default PDFTable;
